import { Component, OnInit } from '@angular/core';
import { SystemUser } from '../system-user';
import { UserService } from '../service/user.service';
import { HttpService } from '../service/http.service';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Booking } from '../booking';
import * as moment from 'moment';
import { STUDENTLANGUAGES } from '../student-languages-list';
import { FaviconService } from '../favicon.service';
import { MessageService } from 'primeng/api';
import { School } from '../school';
import { Country } from '../country';
import { BasicBrugesVndBooking } from '../basic-bruges-vnd-booking';
import { BasicStudent } from '../basic-student';
import { Language } from '../language';
import { environment } from 'src/environments/environment';
import { SalesAgent } from '../sales-agent';
import { forkJoin } from 'rxjs';

@Component({
  selector: 'app-create-bruges-booking',
  templateUrl: './create-bruges-booking.component.html',
  styleUrls: ['./create-bruges-booking.component.css']
})
export class CreateBrugesBookingComponent implements OnInit {

  user!: SystemUser;
  bookingForm!: FormGroup;
  hasError!: boolean;
  hasDateError!: boolean;
  success!: boolean;
  languages: Language[] = STUDENTLANGUAGES;
  createdBooking!: BasicBrugesVndBooking | null;
  formPath!: string;
  bookingsToCompare!: Booking[];
  schools: School[] = [];

  defaultEndDate!: Date;
  sendingRequest = false;
  agents: SalesAgent[] = [];

  constructor(
    public userService: UserService,
    private httpService: HttpService,
    private formBuilder: FormBuilder,
    private favicon: FaviconService,
    private messageService: MessageService
  ) { }

  ngOnInit() {
    this.favicon.sbms();
    this.user = new SystemUser(this.userService.userName, this.userService.roles!);

    let agents = this.httpService.getSalesAgents();
    let schools = this.httpService.getBrugesAndVnd();
    forkJoin([agents, schools]).subscribe(res => {
      this.agents = res[0];
      this.schools = res[1];
      this.bookingForm = this.formBuilder.group({
        lastName: ["", Validators.required],
        firstName: ["", Validators.required],
        startDate: [null, Validators.required],
        endDate: [null, [Validators.required]],
        studentLanguage: [this.languages[1], [Validators.required]],
        school: [this.schools[0], Validators.required],
        agent: [this.agents[0], Validators.required]
      });
    });
    this.httpService.getAllBookings()
    .subscribe(bookings => this.bookingsToCompare = bookings);
  }

  createBooking() {
    if (!this.bookingForm.valid) {
      this.addError("Form invalid", "Please check input value and try again.");
      return;
    }
    this.createdBooking = null;
    this.hasError = false;
    this.success = false;
    if (moment(this.bookingForm.value.startDate).add(5, 'day') > moment(this.bookingForm.value.endDate)) {
      this.addError("Date range invalid", "Students have to stay at least 5 days.");
    } else {
      for(let booking of this.bookingsToCompare){
        if(
          booking.student.firstName.toLowerCase() == this.bookingForm.value.firstName.toLowerCase().trim()
           && booking.student.lastName.toLowerCase() == this.bookingForm.value.lastName.toLowerCase().trim()
        ){
          console.log("duplicated first name and last name");
          this.addWarningDialog("booking with " + this.bookingForm.value.firstName + " " + this.bookingForm.value.lastName + " exist already.", "are you sure to create a booking for this person?");
          return;
        }
      };
      this.submitForm();
    }
  }

  addError(summary: string, detail: string) {
    this.messageService.add({ severity: 'error', summary: summary, detail: detail });
  }

  addSuccess(summary: string, detail: string) {
    this.messageService.add({ severity: 'success', summary: summary, detail: detail });
  }

  addWarningDialog(summary: string, detail: string){
    this.messageService.add({key: "dialog", sticky: true, severity: 'warn', summary: summary, detail: detail})
  }

  copyUrl(){
    document.addEventListener("copy", (e: ClipboardEvent) => {
      e.clipboardData!.setData("text/plain", this.formPath);
      this.addSuccess("URL copy success !", "You copied url: " + e.clipboardData!.getData("text/plain"));
      e.preventDefault();
    
    });
    document.removeEventListener("copy", (e: ClipboardEvent) => {
      e.clipboardData!.setData("text/plain", this.formPath);
      this.addSuccess("URL copy success !", "You copied url: " + e.clipboardData!.getData("text/plain"));
      e.preventDefault();
    });
    document.execCommand("copy");
  }

  updateEndMonth(){
    let startDateMoment = moment(this.bookingForm.value.startDate);
    let defaultEndDateMoment = startDateMoment.add(5, "days");
    this.defaultEndDate = defaultEndDateMoment.toDate();
  }

  onReject(){
    this.messageService.clear("dialog")
    return;
  }

  onConfirm(){
    this.messageService.clear("dialog");
    this.submitForm();
  }

  submitForm(){
    this.sendingRequest = true;
    this.httpService.postBrugesBooking(
      this.createNewBooking()
    ).subscribe(booking => {
      this.success = true;
      this.sendingRequest = false;
      this.addSuccess("Success", "Your request is sent successfully");
      this.createdBooking = booking;
      this.formPath = environment.sbmsBaseUrl + this.getLanguagePath(this.bookingForm.value.studentLanguage) + "/dashboard/" + this.createdBooking.uuid;
      this.bookingForm.reset();
      this.bookingForm.get('studentLanguage')!.setValue(this.languages[1]);
      this.bookingForm.get('school')!.setValue(this.schools[0]);
      this.bookingForm.get('agent')!.setValue(this.agents[0]);
    }, error => {
      this.sendingRequest = false;
      this.addError("HTTP Error", "Form value is valid, but there is an error in sending request to backend.");
      this.hasError = true
    });
  }

  createNewBooking(){
    let booking = new BasicBrugesVndBooking(
      moment(this.bookingForm.value.startDate, "DD/MM/YYYY").utc(true),
      moment(this.bookingForm.value.endDate, "DD/MM/YYYY").utc(true),
      this.bookingForm.value.studentLanguage,
      this.bookingForm.value.school,
      new Country(21, "Belgium"),
      new BasicStudent(
        this.bookingForm.value.firstName.trim(),
        this.bookingForm.value.lastName.trim(),
        this.bookingForm.value.studentLanguage
      ),
      this.bookingForm.value.agent
    );
    return booking;
  }

  getLanguagePath(language: Language){
    if(language.id == 1){
      return "fr";
    } else if(language.id == 2){
      return "en";
    } else if(language.id == 3){
      return "nl";
    } else if(language.id == 4){
      return "es"
    } else {
      return "en";
    }
  }

}
